/**
 * SN-4709 Team Privacy (Inon, Sep 2) — how a team link is split so it can be
 * truncated in the MIDDLE rather than at either end.
 *
 * A registration team link is ~80 characters and carries two things a rep needs
 * to recognise it, at opposite ends:
 *
 *   https://app.sportninja.com/registration/VVV4Qb4epxr5trnY?team_id=BO3H…9qToN
 *   ^ where it goes                                                     ^ which team
 *
 * Tail truncation (plain `text-overflow: ellipsis`) keeps the origin and throws
 * the team uid away; head truncation does the reverse. Both cut something the
 * link is being shown FOR, which is why the field truncates in the middle.
 *
 * The split is a fixed number of trailing characters rather than a measured
 * midpoint on purpose: the head is then ellipsised by CSS against the width the
 * field actually has, so the value re-truncates on resize with no measurement,
 * no ResizeObserver and no re-render. Nothing here knows or needs the pixel
 * width.
 */

/**
 * Characters kept at the end. Eight is enough of the team uid to tell two of a
 * competition's links apart at a glance, and short enough to leave the head the
 * majority of a narrow field.
 */
const TAIL_LENGTH = 8

export interface ShareLinkParts {
  /** Everything up to the tail. The renderer lets CSS ellipsise THIS end. */
  head: string
  /** The last characters, never truncated. */
  tail: string
}

/**
 * Split a link for middle-truncated display.
 *
 * A link too short to be worth truncating comes back whole in `head` with an
 * empty `tail` — splitting an already-short value would put an ellipsis where
 * nothing was ever going to be cut.
 */
export const shareLinkParts = (link: string): ShareLinkParts => {
  // Below twice the tail there is no meaningful middle left to remove.
  if (link.length <= TAIL_LENGTH * 2) {
    return { head: link, tail: '' }
  }

  return {
    head: link.slice(0, -TAIL_LENGTH),
    tail: link.slice(-TAIL_LENGTH),
  }
}
